import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router";
import { precarregarDestino } from "../precarregar-rota.js";
import { barraGuardada, guardarBarra, type EstadoDaBarra } from "../barra-lateral.js";
import { useRitual } from "../estado/ritual.js";
import { useTour } from "../estado/tour.js";
import { aplicarTema, temaGuardado, type Tema } from "../tema.js";
import type { Glifo } from "./glifos.js";
import { Icone } from "./Icone.js";
import { Grifo } from "./Marca.js";
import { Risquinhos } from "./Risquinhos.js";
import { SeletorDeTrilha } from "./TrocarTrilha.js";
import {
  IconeAcervo,
  IconeAjustes,
  IconeAvancar,
  IconeCaderno,
  IconeEstudar,
  IconeInicio,
  IconeMais,
  IconeNumeros,
  IconeRevisar,
  IconeSimulado,
} from "./icones.js";

/**
 * A navegação do tema Caderno (ADR 040).
 *
 * **Desktop:** a lateral é a borda da folha, com os furos da espiral. Marca,
 * seletor de trilha e os nove destinos, com o ativo em marca-texto. Embaixo, o
 * botão de recolher, que deixa só os ícones. Entre 48 e 64rem ela fica sempre
 * recolhida, pela folha.
 *
 * **Celular:** cabeçalho de 64px (marca, trilha, ofensiva em risquinhos) e a
 * ilha flutuante com seis itens. O "Mais" abre um cartão pautado com o resto e
 * a escolha do tema. Nenhum destino deixa de existir por causa da largura
 * (RN-30).
 */

type Destino = { para: string; rotulo: string; glifo: Glifo; exato: boolean };

const TODOS: readonly Destino[] = [
  { para: "/", rotulo: "Início", glifo: IconeInicio, exato: true },
  { para: "/estudar", rotulo: "Estudar", glifo: IconeEstudar, exato: false },
  { para: "/revisar", rotulo: "Revisar", glifo: IconeRevisar, exato: false },
  { para: "/simulado", rotulo: "Simulado", glifo: IconeSimulado, exato: false },
  { para: "/caderno", rotulo: "Caderno", glifo: IconeCaderno, exato: false },
  { para: "/acervo", rotulo: "Acervo", glifo: IconeAcervo, exato: false },
  { para: "/estatisticas", rotulo: "Números", glifo: IconeNumeros, exato: false },
  { para: "/ajustes", rotulo: "Ajustes", glifo: IconeAjustes, exato: false },
];

/**
 * Importar mora só dentro do Acervo, como aba (ADR 041): a porta de entrada
 * das questões é o Início vazio e o fim de sessão, não um destino solto na
 * lateral. O Acervo acende em qualquer aba dele.
 */
function ativo(d: Destino, caminho: string): boolean {
  if (d.para === "/estudar") return caminho.startsWith("/estudar");
  if (d.para === "/simulado") return caminho.startsWith("/simulado");
  if (d.para === "/acervo") return caminho.startsWith("/acervo") || caminho.startsWith("/trilhas");
  return d.exato ? caminho === d.para : caminho.startsWith(d.para);
}

/* ------------------------------------------------------------- desktop --- */

export function useLateral(): [boolean, () => void] {
  const [estado, setEstado] = useState<EstadoDaBarra>(barraGuardada);
  const recolhida = estado === "recolhida";
  return [
    recolhida,
    () => {
      const novo: EstadoDaBarra = recolhida ? "expandida" : "recolhida";
      setEstado(novo);
      guardarBarra(novo);
    },
  ];
}

export function BarraLateral({ recolhida, aoAlternar }: { recolhida: boolean; aoAlternar: () => void }) {
  const { pathname } = useLocation();
  const tour = useTour();
  const texto = recolhida ? "Expandir menu" : "Recolher menu";
  return (
    <aside className="lateral">
      <div className="lateral__corpo">
        <Link to="/" className="lateral__marca" aria-label="Grifo, início">
          <Grifo />
          <Grifo compacto />
        </Link>

        <SeletorDeTrilha />

        <nav className="lateral__nav" aria-label="Seções do Grifo" data-tour="nav">
          {TODOS.map((d) => {
            const liga = ativo(d, pathname);
            return (
              <Link
                key={d.para}
                to={d.para}
                data-tour={d.para === "/revisar" ? "navRevisar" : undefined}
                onClick={d.para === "/revisar" ? () => tour.avancarSe("navRevisar") : undefined}
                className={liga ? "lateral__item lateral__item--ativo" : "lateral__item"}
                aria-current={liga ? "page" : undefined}
                aria-label={d.rotulo}
                title={recolhida ? d.rotulo : undefined}
                onPointerEnter={() => precarregarDestino(d.para)}
                onFocus={() => precarregarDestino(d.para)}
              >
                <Icone glifo={d.glifo} papel="navegacao" />
                <span className="lateral__rotulo t-anotacao" aria-hidden="true">
                  {d.rotulo}
                </span>
              </Link>
            );
          })}
        </nav>

        {/* Com o tour parado, ele pode ser refeito sobre os dados reais (ADR 041). */}
        {tour.passo === null && (
          <button
            type="button"
            className="lateral__refazer"
            title={recolhida ? "Refazer o tour" : undefined}
            aria-label="Refazer o tour"
            onClick={tour.refazer}
          >
            <Icone glifo="placa" papel="navegacao" />
            <span className="lateral__refazer-rotulo" aria-hidden="true">
              refazer o tour
            </span>
          </button>
        )}

        <button
          type="button"
          className="lateral__recolher"
          aria-label={texto}
          aria-expanded={!recolhida}
          title={texto}
          onClick={aoAlternar}
        >
          <Icone glifo={recolhida ? "chevron_duplo_direita" : "chevron_duplo_esquerda"} papel="navegacao" />
          <span className="lateral__recolher-rotulo">{texto}</span>
        </button>
      </div>
    </aside>
  );
}

/* -------------------------------------------------------------- celular --- */

/** O cabeçalho de 64px do celular: marca, trilha e a ofensiva em risquinhos. */
export function CabecalhoMovel() {
  const { ofensiva } = useRitual();
  const dias = ofensiva?.atual ?? 0;
  return (
    <header className="cabecalho-mob">
      <div className="cabecalho-mob__esquerda">
        <Link to="/" aria-label="Grifo, início" className="cabecalho-mob__marca">
          <Grifo tamanho="cabecalho" />
        </Link>
        <SeletorDeTrilha />
      </div>
      {ofensiva && (
        <span className="cabecalho-mob__ofensiva">
          {dias > 0 && <Risquinhos quantidade={dias} maximo={5} />}
          <span className="t-anotacao">
            {dias} {dias === 1 ? "dia" : "dias"}
          </span>
        </span>
      )}
    </header>
  );
}

const ILHA: readonly Destino[] = [
  { para: "/", rotulo: "Início", glifo: IconeInicio, exato: true },
  { para: "/estudar", rotulo: "Estudar", glifo: IconeEstudar, exato: false },
  { para: "/revisar", rotulo: "Revisar", glifo: IconeRevisar, exato: false },
  { para: "/caderno", rotulo: "Caderno", glifo: IconeCaderno, exato: false },
  { para: "/estatisticas", rotulo: "Números", glifo: IconeNumeros, exato: false },
];

const NO_MAIS: readonly (Destino & { descricao: string })[] = [
  { para: "/simulado", rotulo: "Simulado", glifo: IconeSimulado, exato: false, descricao: "uma prova inteira, no tempo dela" },
  { para: "/acervo", rotulo: "Acervo", glifo: IconeAcervo, exato: false, descricao: "importar, trilhas, fontes e marcados" },
  { para: "/ajustes", rotulo: "Ajustes", glifo: IconeAjustes, exato: false, descricao: "tema, conta, IA e modo ônibus" },
];

const TEMAS: readonly { valor: Tema; rotulo: string }[] = [
  { valor: "claro", rotulo: "claro" },
  { valor: "escuro", rotulo: "escuro" },
  { valor: "sistema", rotulo: "aparelho" },
];

export function NavegacaoInferior() {
  const [aberto, setAberto] = useState(false);
  const dialogo = useRef<HTMLDialogElement>(null);
  const { pathname } = useLocation();
  const [tema, setTema] = useState<Tema>(temaGuardado);

  useEffect(() => {
    const d = dialogo.current;
    if (!d) return;
    if (aberto && !d.open) d.showModal();
    if (!aberto && d.open) d.close();
  }, [aberto]);

  const maisAtivo = aberto || NO_MAIS.some((d) => ativo(d, pathname));

  /* O tema é preferência de aparelho, como em Ajustes. */
  function escolherTema(t: Tema) {
    setTema(t);
    aplicarTema(t);
  }

  return (
    <>
      <nav className="ilha" aria-label="Seções do Grifo">
        {ILHA.map((d) => {
          const liga = !aberto && ativo(d, pathname);
          return (
            <NavLink
              key={d.para}
              to={d.para}
              end={d.exato}
              className={liga ? "ilha__item ilha__item--ativo" : "ilha__item"}
              aria-current={liga ? "page" : undefined}
              onPointerEnter={() => precarregarDestino(d.para)}
              onFocus={() => precarregarDestino(d.para)}
            >
              <Icone glifo={d.glifo} papel={liga ? "estado-ligado" : "navegacao"} />
              <span className="ilha__rotulo">{d.rotulo}</span>
            </NavLink>
          );
        })}
        <button
          type="button"
          className={maisAtivo ? "ilha__item ilha__item--ativo" : "ilha__item"}
          aria-haspopup="dialog"
          aria-expanded={aberto}
          onClick={() => setAberto(!aberto)}
        >
          <Icone glifo={IconeMais} papel={maisAtivo ? "estado-ligado" : "navegacao"} />
          <span className="ilha__rotulo">Mais</span>
        </button>
      </nav>

      <dialog
        ref={dialogo}
        className="menu-mais"
        aria-label="Mais seções"
        onClose={() => setAberto(false)}
        onClick={(e) => {
          if (e.target === dialogo.current) setAberto(false);
        }}
      >
        <nav aria-label="Outras seções">
          {NO_MAIS.map((d) => (
            <Link
              key={d.para}
              to={d.para}
              className="menu-mais__item"
              aria-current={ativo(d, pathname) ? "page" : undefined}
              onClick={() => setAberto(false)}
            >
              <Icone glifo={d.glifo} papel="navegacao" />
              <span className="menu-mais__texto">
                <span className="t-cartao">{d.rotulo}</span>
                <span className="t-ajuda menu-mais__descricao">{d.descricao}</span>
              </span>
              <Icone glifo={IconeAvancar} papel="acao" tamanho="p" className="menu-mais__seta" />
            </Link>
          ))}
        </nav>
        <div className="menu-mais__tema" role="radiogroup" aria-label="Tema">
          <span className="t-anotacao t-2">tema</span>
          {TEMAS.map((t) => (
            <button
              key={t.valor}
              type="button"
              role="radio"
              aria-checked={tema === t.valor}
              className="chip-mao"
              onClick={() => escolherTema(t.valor)}
            >
              {t.rotulo}
            </button>
          ))}
        </div>
      </dialog>
    </>
  );
}
